/**
 * Modal for registering a robot model with step-by-step URDF + mesh upload.
 */

import { useState, useRef, useCallback, DragEvent } from "react";
import {
  Modal,
  Stepper,
  Button,
  Group,
  Text,
  Box,
  Stack,
  Paper,
  TextInput,
  ScrollArea,
  Badge,
  Alert,
  Progress,
} from "@mantine/core";
import {
  IconUpload,
  IconCheck,
  IconX,
  IconFile,
  IconFolder,
  IconFileZip,
  IconAlertCircle,
  IconRobot,
} from "@tabler/icons-react";
import JSZip from "jszip";

interface MeshRef {
  fullPath: string;
  relativePath: string;
  filename: string;
  matched: boolean;
  matchedFile: File | null;
}

interface RegisterRobotModalProps {
  opened: boolean;
  onClose: () => void;
  onUploadComplete: (result: {
    success: boolean;
    message: string;
    notices?: string[];
  }) => void;
}

/**
 * Parse URDF XML content to extract mesh file references.
 */
function parseUrdfMeshRefs(xmlString: string): MeshRef[] {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlString, "text/xml");
  const meshElements = doc.querySelectorAll("mesh");
  const refs = new Map<string, MeshRef>();

  meshElements.forEach((el) => {
    const filename = el.getAttribute("filename");
    if (!filename) return;
    // Extract relative path after "package://<package_name>/"
    const match = filename.match(/^package:\/\/[^/]+\/(.+)$/);
    const relativePath = match ? match[1] : filename;
    const basename = relativePath.split("/").pop() || relativePath;
    if (!refs.has(filename)) {
      refs.set(filename, {
        fullPath: filename,
        relativePath,
        filename: basename,
        matched: false,
        matchedFile: null,
      });
    }
  });
  return Array.from(refs.values());
}

/**
 * Recursively traverse a dropped directory entry to collect files.
 */
async function traverseFileTree(
  entry: FileSystemEntry,
  path: string = ""
): Promise<File[]> {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    const file = await new Promise<File>((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });
    // Define webkitRelativePath property on the file object to preserve folder structure
    Object.defineProperty(file, "webkitRelativePath", {
      value: path + file.name,
      writable: true,
      enumerable: true,
      configurable: true,
    });
    return [file];
  }
  if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const reader = dirEntry.createReader();
    const entries: FileSystemEntry[] = [];
    // readEntries may return results in batches
    let batch: FileSystemEntry[];
    do {
      batch = await new Promise<FileSystemEntry[]>((resolve, reject) => {
        reader.readEntries(resolve, reject);
      });
      entries.push(...batch);
    } while (batch.length > 0);

    const allFiles: File[] = [];
    for (const child of entries) {
      const childFiles = await traverseFileTree(
        child,
        path + entry.name + "/"
      );
      allFiles.push(...childFiles);
    }
    return allFiles;
  }
  return [];
}

const MESH_EXTENSIONS = new Set([
  ".stl",
  ".dae",
  ".obj",
  ".glb",
  ".gltf",
]);

/** Extensions for materials and textures that meshes may reference. */
const TEXTURE_EXTENSIONS = new Set([
  ".mtl",
  ".png",
  ".jpg",
  ".jpeg",
  ".tga",
  ".bmp",
  ".tiff",
  ".tif",
]);

function isAssetFile(filename: string): boolean {
  const ext = filename.slice(filename.lastIndexOf(".")).toLowerCase();
  return MESH_EXTENSIONS.has(ext) || TEXTURE_EXTENSIONS.has(ext);
}

/**
 * Parse a mesh/material file's text content and return relative paths
 * of files it depends on (MTL from OBJ, textures from MTL, images from DAE).
 */
function parseMeshDeps(filename: string, content: string): string[] {
  const deps: string[] = [];
  const ext = filename.slice(filename.lastIndexOf(".")).toLowerCase();

  if (ext === ".obj") {
    // OBJ → mtllib <file>
    for (const m of content.matchAll(/^\s*mtllib\s+(.+)$/gm)) {
      deps.push(m[1].trim());
    }
  } else if (ext === ".mtl") {
    // MTL → map_Kd, map_Ka, map_Ks, map_Ns, map_d, map_bump, bump, disp, decal
    for (const m of content.matchAll(
      /^\s*(?:map_Kd|map_Ka|map_Ks|map_Ns|map_d|map_bump|bump|disp|decal)\s+(.+)$/gm
    )) {
      const p = m[1].trim();
      if (!p.startsWith("http://") && !p.startsWith("https://")) {
        deps.push(p);
      }
    }
  } else if (ext === ".dae") {
    // DAE → <init_from>file</init_from>
    // Only consider values with a file extension (contains a dot).
    // Values without a dot are internal Collada IDs (e.g. "file1",
    // "base_colorpng") referencing <library_images>/<surface> elements.
    for (const m of content.matchAll(/<init_from>\s*([^<]+?)\s*<\/init_from>/g)) {
      const p = m[1].trim();
      const basename = p.split("/").pop() || p;
      if (
        !p.startsWith("http://") &&
        !p.startsWith("https://") &&
        !p.startsWith("package://") &&
        !p.startsWith("file://") &&
        basename.includes(".")
      ) {
        deps.push(p);
      }
    }
  }
  return deps;
}

export function RegisterRobotModal({
  opened,
  onClose,
  onUploadComplete,
}: RegisterRobotModalProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [mode, setMode] = useState<"urdf" | "zip" | null>(null);

  // Step 0: URDF
  const [urdfFile, setUrdfFile] = useState<File | null>(null);
  const [urdfContent, setUrdfContent] = useState("");
  const [requiredMeshes, setRequiredMeshes] = useState<MeshRef[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  // Step 0: ZIP quick path
  const [zipFile, setZipFile] = useState<File | null>(null);

  // Step 1: Meshes
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // Step 2: Submit
  const [moduleName, setModuleName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const urdfInputRef = useRef<HTMLInputElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);
  const meshFileInputRef = useRef<HTMLInputElement>(null);
  const meshDirInputRef = useRef<HTMLInputElement>(null);

  const resetState = useCallback(() => {
    setActiveStep(0);
    setMode(null);
    setUrdfFile(null);
    setUrdfContent("");
    setRequiredMeshes([]);
    setParseError(null);
    setZipFile(null);
    setUploadedFiles([]);
    setIsDragOver(false);
    setModuleName("");
    setUploading(false);
    setUploadProgress(0);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  // --- Step 0: URDF file handling ---
  const handleUrdfFile = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result;
      if (typeof content !== "string") return;

      setUrdfContent(content);
      setUrdfFile(file);
      setParseError(null);

      const meshRefs = parseUrdfMeshRefs(content);
      setRequiredMeshes(meshRefs);
      setModuleName(file.name.replace(/\.urdf$/i, ""));
      setMode("urdf");

      if (meshRefs.length === 0) {
        // No meshes needed, skip to review
        setActiveStep(2);
      } else {
        setActiveStep(1);
      }
    };
    reader.onerror = () => {
      setParseError("Failed to read file");
    };
    reader.readAsText(file);
  }, []);

  const handleUrdfInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleUrdfFile(file);
      e.target.value = "";
    },
    [handleUrdfFile]
  );

  // --- Step 0: ZIP quick path ---
  const handleZipFile = useCallback((file: File) => {
    setZipFile(file);
    setMode("zip");
    setModuleName(file.name.replace(/\.zip$/i, ""));
    setActiveStep(2);
  }, []);

  const handleZipInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleZipFile(file);
      e.target.value = "";
    },
    [handleZipFile]
  );

  // --- Step 1: Mesh + texture file matching ---
  const matchMeshFiles = useCallback(
    (files: File[], filterByExtension = true) => {
      setUploadedFiles((prev) => {
        // Accept mesh files AND texture/material files
        // When a directory is selected, filterByExtension=false to keep everything
        const newFiles = filterByExtension
          ? files.filter((f) => isAssetFile(f.name))
          : files;
        const allFiles = [...prev, ...newFiles];
        // Build a map of basename -> files for matching
        const filesByName = new Map<string, File[]>();
        for (const f of allFiles) {
          const name = f.name.toLowerCase();
          if (!filesByName.has(name)) filesByName.set(name, []);
          filesByName.get(name)!.push(f);
        }

        setRequiredMeshes((meshes) => {
          const updated = meshes.map((mesh) => {
            const candidates = filesByName.get(mesh.filename.toLowerCase());
            if (candidates && candidates.length > 0) {
              // Prefer path-based matching if webkitRelativePath is available
              let bestMatch = candidates[0];
              for (const c of candidates) {
                if (
                  c.webkitRelativePath &&
                  c.webkitRelativePath.includes(
                    mesh.relativePath.replace(/^meshes\//, "")
                  )
                ) {
                  bestMatch = c;
                  break;
                }
              }
              return { ...mesh, matched: true, matchedFile: bestMatch };
            }
            return mesh;
          });

          // Discover texture deps from matched mesh/material files
          // and add them to the required list if not already present
          const existingPaths = new Set(updated.map((m) => m.filename.toLowerCase()));
          const newDeps: MeshRef[] = [];

          for (const mesh of updated) {
            if (!mesh.matched || !mesh.matchedFile) continue;
            const ext = mesh.filename.slice(mesh.filename.lastIndexOf(".")).toLowerCase();
            if (ext !== ".obj" && ext !== ".mtl" && ext !== ".dae") continue;

            // Read file synchronously via the already-loaded File reference
            // We'll parse it async and update state later
            const file = mesh.matchedFile;
            const parentDir = mesh.relativePath.includes("/")
              ? mesh.relativePath.substring(0, mesh.relativePath.lastIndexOf("/") + 1)
              : "";

            file.text().then((content) => {
              const deps = parseMeshDeps(mesh.filename, content);
              const toAdd: MeshRef[] = [];
              for (const dep of deps) {
                const depBasename = dep.split("/").pop() || dep;
                const depRelPath = parentDir + dep;
                if (existingPaths.has(depBasename.toLowerCase())) continue;
                existingPaths.add(depBasename.toLowerCase());

                // Check if already uploaded
                const candidates = filesByName.get(depBasename.toLowerCase());
                const matchedFile = candidates?.[0] ?? null;

                toAdd.push({
                  fullPath: depRelPath,
                  relativePath: depRelPath,
                  filename: depBasename,
                  matched: matchedFile !== null,
                  matchedFile,
                });
              }
              if (toAdd.length > 0) {
                setRequiredMeshes((prev) => {
                  const prevPaths = new Set(prev.map((m) => m.filename.toLowerCase()));
                  const deduped = toAdd.filter((d) => !prevPaths.has(d.filename.toLowerCase()));
                  return deduped.length > 0 ? [...prev, ...deduped] : prev;
                });
              }
            });
          }

          return newDeps.length > 0 ? [...updated, ...newDeps] : updated;
        });

        return allFiles;
      });
    },
    []
  );

  const handleMeshFilesInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) matchMeshFiles(files);
      e.target.value = "";
    },
    [matchMeshFiles],
  );

  /** Directory input handler — passes ALL files (no extension filter)
   *  so that textures, materials, and other assets are included. */
  const handleMeshDirInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) matchMeshFiles(files, false);
      e.target.value = "";
    },
    [matchMeshFiles],
  );

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const items = e.dataTransfer.items;
      const collectedFiles: File[] = [];

      if (items) {
        const entries: FileSystemEntry[] = [];
        for (let i = 0; i < items.length; i++) {
          const entry = items[i].webkitGetAsEntry?.();
          if (entry) entries.push(entry);
        }

        for (const entry of entries) {
          const files = await traverseFileTree(entry);
          collectedFiles.push(...files);
        }
      } else {
        // Fallback
        collectedFiles.push(...Array.from(e.dataTransfer.files));
      }

      if (collectedFiles.length > 0) {
        // Drag-and-drop may include directories, so don't filter by extension
        matchMeshFiles(collectedFiles, false);
      }
    },
    [matchMeshFiles]
  );

  // Also handle URDF drop on step 0
  const handleUrdfDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      const urdf = files.find((f) => f.name.toLowerCase().endsWith(".urdf"));
      const zip = files.find((f) => f.name.toLowerCase().endsWith(".zip"));

      if (urdf) {
        handleUrdfFile(urdf);
      } else if (zip) {
        handleZipFile(zip);
      }
    },
    [handleUrdfFile, handleZipFile]
  );

  // --- Step 2: Submit ---
  const handleSubmit = useCallback(async () => {
    setUploading(true);
    setUploadProgress(0);

    try {
      let blob: Blob;
      let filename: string;

      if (mode === "zip" && zipFile) {
        blob = zipFile;
        filename = zipFile.name;
      } else {
        // Build zip from URDF + meshes
        const zip = new JSZip();
        const pkgName = moduleName || "robot_module";

        // Add URDF
        zip.file(`${pkgName}/${urdfFile!.name}`, urdfContent);

        // Add matched meshes and textures preserving relative paths
        for (const mesh of requiredMeshes) {
          if (mesh.matched && mesh.matchedFile) {
            zip.file(`${pkgName}/${mesh.relativePath}`, mesh.matchedFile);
          }
        }

        // Also include any uploaded files that are not already in the
        // required list (e.g. textures discovered from directory upload)
        const includedPaths = new Set(
          requiredMeshes
            .filter((m) => m.matched)
            .map((m) => m.relativePath.toLowerCase()),
        );
        for (const file of uploadedFiles) {
          const relPath = file.webkitRelativePath || file.name;
          // Strip leading directory name that matches the package
          const parts = relPath.split("/");
          const innerPath = parts.length > 1 ? parts.slice(1).join("/") : relPath;
          if (!includedPaths.has(innerPath.toLowerCase())) {
            zip.file(`${pkgName}/${innerPath}`, file);
            includedPaths.add(innerPath.toLowerCase());
          }
        }

        blob = await zip.generateAsync(
          { type: "blob" },
          (metadata) => {
            setUploadProgress(Math.round(metadata.percent));
          }
        );
        filename = `${pkgName}.zip`;
      }

      const formData = new FormData();
      formData.append("file", blob, filename);
      formData.append("name", moduleName || "uploaded_module");
      // Tag the upload with the current session so anonymous users can see
      // their own uploaded modules within this session.
      if (window.sessionId) {
        formData.append("session_id", window.sessionId);
      }

      const response = await fetch("/api/upload-module", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        // Server returned an error (e.g. nginx 413 Too Large)
        let errorMsg = `Server error: ${response.status} ${response.statusText}`;
        const contentType = response.headers.get("content-type") || "";
        if (contentType.includes("application/json")) {
          const errBody = await response.json();
          errorMsg = errBody.error || errorMsg;
        }
        onUploadComplete({ success: false, message: errorMsg });
        handleClose();
        return;
      }

      const result = await response.json();

      // Repairs the server made to the upload, shown alongside the outcome.
      const notices: string[] = Array.isArray(result.notices)
        ? result.notices
        : [];

      if (result.success) {
        const names = Array.isArray(result.module_name)
          ? result.module_name
          : [result.module_name];
        onUploadComplete({
          success: true,
          message:
            names.length === 1
              ? `Module "${names[0]}" registered successfully`
              : `${names.length} modules registered successfully`,
          notices,
        });
      } else {
        onUploadComplete({
          success: false,
          message: result.error || "Registration failed",
          notices,
        });
      }
      handleClose();
    } catch (err) {
      onUploadComplete({
        success: false,
        message: `Registration failed: ${err instanceof Error ? err.message : "network error"}`,
      });
      setUploading(false);
    }
  }, [
    mode,
    zipFile,
    urdfFile,
    urdfContent,
    moduleName,
    requiredMeshes,
    onUploadComplete,
    handleClose,
  ]);

  const matchedCount = requiredMeshes.filter((m) => m.matched).length;
  const totalMeshes = requiredMeshes.length;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Register Robot Model"
      centered
      size="lg"
    >
      <Stepper
        active={activeStep}
        onStepClick={(step) => {
          // Only allow going back
          if (step < activeStep) setActiveStep(step);
        }}
        mb="lg"
        size="sm"
      >
        <Stepper.Step label="Select File" description="URDF or ZIP" />
        <Stepper.Step
          label="Meshes"
          description={
            mode === "zip" ? "Skipped" : `${matchedCount}/${totalMeshes}`
          }
        />
        <Stepper.Step label="Register" description="Review & submit" />
      </Stepper>

      {/* Step 0: File Selection */}
      {activeStep === 0 && (
        <Stack gap="md">
          {/* Drop zone for URDF or ZIP */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleUrdfDrop}
            style={{
              border: `2px dashed ${isDragOver ? "var(--mantine-color-blue-5)" : "var(--mantine-color-gray-4)"}`,
              borderRadius: 8,
              padding: 32,
              textAlign: "center",
              backgroundColor: isDragOver
                ? "var(--mantine-color-blue-0)"
                : "transparent",
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            onClick={() => urdfInputRef.current?.click()}
          >
            <IconUpload
              size={40}
              color="var(--mantine-color-gray-5)"
              style={{ marginBottom: 8 }}
            />
            <Text size="sm" fw={500}>
              Drop URDF file here or click to select
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              URDF file will be parsed to find required meshes
            </Text>
          </Box>

          <input
            ref={urdfInputRef}
            type="file"
            accept=".urdf"
            style={{ display: "none" }}
            onChange={handleUrdfInputChange}
          />

          {/* ZIP quick path */}
          <Group justify="center">
            <Text size="xs" c="dimmed">
              or
            </Text>
          </Group>

          <Button
            variant="light"
            color="gray"
            leftSection={<IconFileZip size={16} />}
            onClick={() => zipInputRef.current?.click()}
            fullWidth
          >
            Upload ZIP archive (URDF + meshes)
          </Button>
          <input
            ref={zipInputRef}
            type="file"
            accept=".zip"
            style={{ display: "none" }}
            onChange={handleZipInputChange}
          />

          {parseError && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              variant="light"
            >
              {parseError}
            </Alert>
          )}
        </Stack>
      )}

      {/* Step 1: Mesh Upload */}
      {activeStep === 1 && (
        <Stack gap="md">
          <Group justify="space-between">
            <Text size="sm" fw={500}>
              Required Meshes ({matchedCount}/{totalMeshes} matched)
            </Text>
            {matchedCount === totalMeshes && (
              <Badge color="green" variant="light">
                All matched
              </Badge>
            )}
          </Group>

          {/* Drop zone for meshes */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${isDragOver ? "var(--mantine-color-blue-5)" : "var(--mantine-color-gray-4)"}`,
              borderRadius: 8,
              padding: 20,
              textAlign: "center",
              backgroundColor: isDragOver
                ? "var(--mantine-color-blue-0)"
                : "transparent",
              transition: "all 0.15s ease",
            }}
          >
            <IconUpload
              size={28}
              color="var(--mantine-color-gray-5)"
              style={{ marginBottom: 4 }}
            />
            <Text size="sm">
              Drop mesh files/folders here (meshes, textures, materials)
            </Text>
            <Group justify="center" gap="xs" mt="xs">
              <Button
                size="xs"
                variant="light"
                leftSection={<IconFile size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  meshFileInputRef.current?.click();
                }}
              >
                Select Files
              </Button>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconFolder size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  meshDirInputRef.current?.click();
                }}
              >
                Select Directory
              </Button>
            </Group>
          </Box>

          <input
            ref={meshFileInputRef}
            type="file"
            multiple
            accept=".stl,.dae,.obj,.glb,.gltf,.mtl,.png,.jpg,.jpeg,.tga,.bmp,.tiff"
            style={{ display: "none" }}
            onChange={handleMeshFilesInput}
          />
          <input
            ref={meshDirInputRef}
            type="file"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
            style={{ display: "none" }}
            onChange={handleMeshDirInput}
          />

          {/* Mesh list */}
          <ScrollArea.Autosize mah={250}>
            <Stack gap={4}>
              {requiredMeshes.map((mesh, idx) => (
                <Paper
                  key={idx}
                  withBorder
                  p="xs"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {mesh.matched ? (
                    <IconCheck size={16} color="var(--mantine-color-green-6)" />
                  ) : (
                    <IconX size={16} color="var(--mantine-color-red-5)" />
                  )}
                  <Text
                    size="xs"
                    style={{
                      flex: 1,
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                    }}
                  >
                    {mesh.relativePath}
                  </Text>
                  <Badge
                    size="xs"
                    color={mesh.matched ? "green" : "red"}
                    variant="light"
                  >
                    {mesh.matched ? "Matched" : "Missing"}
                  </Badge>
                </Paper>
              ))}
            </Stack>
          </ScrollArea.Autosize>

          {uploadedFiles.length > 0 && (
            <Text size="xs" c="dimmed">
              {uploadedFiles.length} file(s) uploaded
            </Text>
          )}

          <Group justify="space-between">
            <Button
              variant="default"
              size="xs"
              onClick={() => setActiveStep(0)}
            >
              Back
            </Button>
            <Group gap="xs">
              {matchedCount < totalMeshes && (
                <Button
                  variant="subtle"
                  size="xs"
                  color="orange"
                  onClick={() => setActiveStep(2)}
                >
                  Continue with missing meshes
                </Button>
              )}
              <Button
                size="xs"
                onClick={() => setActiveStep(2)}
                disabled={matchedCount === 0}
              >
                Next
              </Button>
            </Group>
          </Group>
        </Stack>
      )}

      {/* Step 2: Review & Submit */}
      {activeStep === 2 && (
        <Stack gap="md">
          <TextInput
            label="Module Name"
            value={moduleName}
            onChange={(e) => setModuleName(e.currentTarget.value)}
            placeholder="e.g. my_robot"
          />

          <Paper withBorder p="sm">
            <Stack gap="xs">
              <Group gap="xs">
                <IconRobot size={16} />
                <Text size="sm" fw={500}>
                  Summary
                </Text>
              </Group>
              <Text size="xs">
                <strong>File:</strong>{" "}
                {mode === "zip"
                  ? zipFile?.name
                  : urdfFile?.name}
              </Text>
              {mode === "urdf" && totalMeshes > 0 && (
                <Text size="xs">
                  <strong>Meshes:</strong> {matchedCount}/{totalMeshes} matched
                  {matchedCount < totalMeshes && (
                    <Text span c="orange" size="xs">
                      {" "}
                      ({totalMeshes - matchedCount} missing)
                    </Text>
                  )}
                </Text>
              )}
              {mode === "urdf" && totalMeshes === 0 && (
                <Text size="xs" c="dimmed">
                  No mesh files referenced in URDF
                </Text>
              )}
              {mode === "zip" && (
                <Text size="xs" c="dimmed">
                  ZIP archive will be uploaded as-is
                </Text>
              )}
            </Stack>
          </Paper>

          {matchedCount < totalMeshes && mode === "urdf" && totalMeshes > 0 && (
            <Alert
              color="orange"
              icon={<IconAlertCircle size={16} />}
              variant="light"
            >
              Some meshes are missing. The model may not render correctly.
            </Alert>
          )}

          {uploading && (
            <Progress value={uploadProgress} size="sm" animated />
          )}

          <Group justify="space-between">
            <Button
              variant="default"
              size="xs"
              onClick={() =>
                setActiveStep(
                  mode === "zip" || totalMeshes === 0 ? 0 : 1
                )
              }
              disabled={uploading}
            >
              Back
            </Button>
            <Button
              size="xs"
              onClick={handleSubmit}
              loading={uploading}
              leftSection={<IconCheck size={14} />}
              disabled={!moduleName.trim()}
            >
              Register
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
